/**
 * The closed widget: the mascot, its greeting bubble, and the turn that shows
 * the short phrases (ADR-0059).
 *
 * - "greeting" mode: the bubble appears ~3 s after load; hiding it starts the
 *   turn. "compact" mode: straight to the turn.
 * - Phones never show the bubble (it would cover the page).
 * - Once the visitor hides the bubble or opens the chat, it stays away for
 *   24 h in every tab of the site (like Intercom or Crisp); the avatar keeps
 *   turning with the phrases meanwhile.
 * - With prefers-reduced-motion there is no turn; the mascot stays still.
 * - Clicking the mascot or the bubble's text opens the chat. The chat never
 *   opens on its own.
 * - A person's message the visitor hasn't seen (ADR-0097): a count on the
 *   mascot, the message in the bubble (not on phones), the count in the
 *   button's name, and one announcement. The turn stops meanwhile so the
 *   count stays in view.
 */

import { withFallback } from './assets.js';

const GREETING_DELAY_MS = 3000;
const FRONT_MS = 2800;
const BACK_MS = 1800;
const MOBILE_QUERY = '(max-width: 640px)';
const GREETING_QUIET_MS = 24 * 60 * 60 * 1000;
const GREETING_KEY = 'chatbot-greeting-hidden-until';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const PREVIEW_LENGTH = 140;

function el(tag, className, attributes = {}) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  for (const [name, value] of Object.entries(attributes)) node.setAttribute(name, value);
  return node;
}

function closeIcon(size) {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  for (const [name, value] of Object.entries({
    width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
    'stroke-width': '2', 'stroke-linecap': 'round', 'aria-hidden': 'true',
  })) svg.setAttribute(name, value);
  const path = document.createElementNS(ns, 'path');
  path.setAttribute('d', 'M6 6l12 12M18 6L6 18');
  svg.appendChild(path);
  return svg;
}

/**
 * @returns {{ element: HTMLElement, show: Function, hide: Function, focus: Function, notify: Function }}
 */
export function createLauncher({ config, t, images, storage, onOpen, win = window }) {
  const name = config.bot_name;
  const phrases = (config.rotating_phrases || []).filter(Boolean);
  const launcherImage = config.launcher_image_url || images.launcher;
  const avatarImage = config.avatar_url || images.avatar;

  const root = el('div', 'launcher-area');

  // --- Greeting bubble -----------------------------------------------------
  const bubble = el('div', 'greeting');
  bubble.hidden = true;
  const bubbleText = el('button', 'greeting__text', { type: 'button' });
  const bubbleName = el('span', 'greeting__name');
  bubbleName.textContent = name;
  const bubbleMessage = el('span', 'greeting__message');
  bubbleMessage.textContent = config.greeting || '';
  bubbleText.append(bubbleName, bubbleMessage);
  const bubbleClose = el('button', 'greeting__close icon-button', { type: 'button', 'aria-label': t('hideGreeting') });
  bubbleClose.appendChild(closeIcon(16));
  bubble.append(bubbleText, bubbleClose);

  // --- Mascot, with the phrase on its back -----------------------------------
  const openLabel = t('openChat', { name });
  const mascot = el('button', 'mascot', { type: 'button', 'aria-label': openLabel, 'aria-expanded': 'false' });
  const card = el('span', 'mascot__card');
  const front = el('span', 'mascot__face mascot__face--front');
  if (launcherImage) {
    const img = withFallback(el('img', 'mascot__image', { alt: '', src: launcherImage, width: '128', height: '140', decoding: 'async' }), images.launcher);
    front.appendChild(img);
  }
  const back = el('span', 'mascot__face mascot__face--back', { 'aria-hidden': 'true' });
  const disc = el('span', 'mascot__disc');
  if (avatarImage) {
    disc.appendChild(withFallback(el('img', 'mascot__avatar', { alt: '', src: avatarImage, width: '40', height: '40', decoding: 'async' }), images.avatar));
  }
  const phrase = el('span', 'mascot__phrase');
  disc.appendChild(phrase);
  back.appendChild(disc);
  card.append(front, back);
  const badge = el('span', 'mascot__badge', { 'aria-hidden': 'true' });
  badge.hidden = true;
  mascot.append(card, badge);
  // Announces a person's message once; the count itself is in the button's name.
  const announcer = el('span', 'visually-hidden', { role: 'status' });

  root.append(bubble, mascot, announcer);

  // --- Behaviour -------------------------------------------------------------
  const media = (query) => (typeof win.matchMedia === 'function' ? win.matchMedia(query).matches : false);
  const greetingAllowed = () => config.launcher_mode !== 'compact'
    && Boolean(config.greeting)
    && !media(MOBILE_QUERY)
    && !storage.isQuiet();

  let timer = null;
  let flipped = false;
  let index = -1;
  let turning = false;
  let unread = 0;
  const label = () => {
    if (unread === 0) return openLabel;
    return unread === 1 ? t('unreadOne', { name }) : t('unreadMany', { name, count: unread });
  };

  const stopTurn = () => {
    turning = false;
    win.clearTimeout(timer);
    flipped = false;
    mascot.classList.remove('mascot--flipped');
    mascot.setAttribute('aria-label', label());
  };

  const step = () => {
    if (!turning) return;
    if (flipped) {
      flipped = false;
    } else {
      index = (index + 1) % phrases.length;
      phrase.textContent = phrases[index];
      flipped = true;
    }
    mascot.classList.toggle('mascot--flipped', flipped);
    // WCAG 2.5.3 (label in name): while the phrase shows on the button, its
    // name starts with it — a voice user says what they see.
    mascot.setAttribute('aria-label', flipped ? `${phrases[index]} · ${openLabel}` : openLabel);
    timer = win.setTimeout(step, flipped ? BACK_MS : FRONT_MS);
  };

  const startTurn = () => {
    if (turning || unread > 0 || phrases.length === 0 || media(REDUCED_MOTION_QUERY)) return;
    turning = true;
    timer = win.setTimeout(step, FRONT_MS);
  };

  const hideGreeting = () => {
    bubble.hidden = true;
    storage.quiet();
    startTurn();
  };

  bubbleClose.addEventListener('click', () => {
    hideGreeting();
    mascot.focus();
  });
  // Opening the chat also means the visitor found it: no greeting today.
  const open = () => {
    storage.quiet();
    onOpen();
  };
  bubbleText.addEventListener('click', open);
  mascot.addEventListener('click', open);

  // A hidden tab should not spin a timer for nobody.
  win.document?.addEventListener?.('visibilitychange', () => {
    if (win.document.hidden) stopTurn();
    else if (bubble.hidden && !root.hidden) startTurn();
  });

  const show = () => {
    root.hidden = false;
    mascot.setAttribute('aria-expanded', 'false');
    if (!bubble.hidden) return;
    startTurn();
  };

  const hide = () => {
    root.hidden = true;
    mascot.setAttribute('aria-expanded', 'true');
    bubble.hidden = true;
    stopTurn();
  };

  // First appearance: the greeting after a short delay, or the turn.
  if (greetingAllowed()) {
    win.setTimeout(() => {
      if (root.hidden) return;
      bubble.hidden = false;
    }, GREETING_DELAY_MS);
  } else {
    startTurn();
  }

  /** A person's unseen messages: `count` 0 clears it. */
  const notify = (count, message) => {
    unread = count;
    badge.hidden = count === 0;
    badge.textContent = count > 9 ? '9+' : String(count);
    if (count === 0) {
      announcer.textContent = '';
      // The bubble showed the message: it goes back to the greeting, hidden.
      if (bubble.classList.contains('greeting--message')) {
        bubble.classList.remove('greeting--message');
        bubble.hidden = true;
        bubbleName.textContent = name;
        bubbleMessage.textContent = config.greeting || '';
      }
      mascot.setAttribute('aria-label', openLabel);
      if (!root.hidden && bubble.hidden) startTurn();
      return;
    }
    stopTurn();
    if (message) {
      const who = message.advisor_display_name || name;
      announcer.textContent = t('newMessageFrom', { name: who });
      if (!media(MOBILE_QUERY)) {
        const textOf = message.text || '';
        bubbleName.textContent = who;
        bubbleMessage.textContent = textOf.length > PREVIEW_LENGTH ? `${textOf.slice(0, PREVIEW_LENGTH - 1)}…` : textOf;
        bubble.classList.add('greeting--message');
        bubble.hidden = root.hidden;
      }
    }
  };

  return { element: root, show, hide, focus: () => mascot.focus(), notify };
}

/**
 * Remembers, for 24 h and across every tab of the site, that the greeting
 * should stay away. localStorage on the customer's domain (first-party);
 * tolerant of browsers or modes that forbid it — then the greeting simply
 * shows again next time.
 */
export function greetingMemory(win = window, now = () => Date.now()) {
  return {
    isQuiet() {
      try {
        return Number(win.localStorage.getItem(GREETING_KEY) || 0) > now();
      } catch {
        return false;
      }
    },
    quiet() {
      try {
        win.localStorage.setItem(GREETING_KEY, String(now() + GREETING_QUIET_MS));
      } catch { /* private mode: not remembered */ }
    },
  };
}
